import axios from 'axios';
import { API_URL } from '../config/api';
import { UserCreate } from '../types/models';

// Email verification API service
export interface EmailVerificationResponse {
  message: string;
}

export interface VerificationStatusResponse {
  email_verified: boolean;
  email: string;
  verification_sent_at: string | null;
  can_resend: boolean;
  remaining_minutes: number;
}

class EmailVerificationService {
  private api = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add auth token to requests
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Register a new user with email verification
   */
  async register(userData: UserCreate): Promise<any> {
    try {
      const response = await this.api.post('/register', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || 'Error en el registro. Intenta nuevamente.'
      );
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.api.post(`/verify-email/${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || 'Token de verificación inválido o expirado'
      );
    }
  }

  /**
   * Resend verification email
   */
  async resendVerification(): Promise<EmailVerificationResponse> {
    try {
      const response = await this.api.post('/resend-verification');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || 'Error al reenviar email de verificación'
      );
    }
  }

  /**
   * Get verification status for current user
   */
  async getVerificationStatus(): Promise<VerificationStatusResponse> {
    try {
      const response = await this.api.get('/verification-status');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || 'Error al obtener estado de verificación'
      );
    }
  }
}

export const emailVerificationService = new EmailVerificationService();
export default emailVerificationService;
